import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ReactNode } from 'react';

export interface AreaCode {
  areaCodeId: any;
  cityName: ReactNode;
  cityId: any;
  code: string;
  areaCodeName: string;
  id: number;
}

interface AreaCodeState {
  data: AreaCode[];
  loading: boolean;
  error: string | null;
}

const PORT = import.meta.env.VITE_USERS_PORT;
const URL = import.meta.env.VITE_API_USERS_URL;

// Diet Mapping Code
export const fetchAreaCode = createAsyncThunk<AreaCode[], void, { rejectValue: string }>(
  'AreaCode/fetchAreaCode',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get<{ data: AreaCode[] }>('/get_all_area_code', PORT, URL);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const createAreaCode = createAsyncThunk<AreaCode, any, { rejectValue: string }>(
  'AreaCode/createAreaCode',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.post<any>('create_area_code', params, PORT, URL);
      if (response.data.db_status !== 'success') {
        return rejectWithValue(response.data.message);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);
export const getByIdAreaCode = createAsyncThunk<
  AreaCode[],
  { id: number | string },
  { rejectValue: string }
>('AreaCode/getByIdAreaCode', async (params, { rejectWithValue }) => {
  try {
    const config = { params };
    const response = await apiService.get<any>('get_by_id_area_code', PORT, URL, config);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred');
  }
});
export const updateAreaCode = createAsyncThunk<AreaCode, any, { rejectValue: string }>(
  'AreaCode/updateAreaCode',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.put<{ data: AreaCode }>(
        'update_area_code',
        params,
        PORT,
        URL,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  },
);

export const softDeleteAreaCode: any = createAsyncThunk<number, number>(
  'DeleteAreaCode/softDeleteAreaCode',
  async (id) => {
    try {
      const response = await apiService.patch(`soft_delete_area_code?id=${id}`, {}, PORT, URL);

      return response;
    } catch (error: any) {
      return error?.message || 'An error occurred while deleting cuisine type';
    }
  },
);

export const AreaCodeSlice = createSlice({
  name: 'AreaCode',
  initialState: {
    data: [],
    loading: false,
    error: null,
  } as AreaCodeState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Diet Mapping Code
      .addCase(fetchAreaCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAreaCode.fulfilled, (state, action: PayloadAction<AreaCode[]>) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchAreaCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(createAreaCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAreaCode.fulfilled, (state, action: PayloadAction<AreaCode>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createAreaCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(getByIdAreaCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getByIdAreaCode.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getByIdAreaCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load AreaCode';
      })
      .addCase(updateAreaCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAreaCode.fulfilled, (state, action: PayloadAction<AreaCode>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(updateAreaCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export default AreaCodeSlice.reducer;
